import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsCompact } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import { BusinessSwitcher } from './business-switcher';
import { useNavActions } from './nav-actions.store';
import { useSidebarStore } from './sidebar.store';
import {
  useContainsActive,
  useIsActive,
  useVisibleNavigation,
  type ResolvedNavItem,
} from './use-navigation';

/**
 * One sidebar, two presentations, arbitrary depth.
 *
 * The old one rendered `sidebarContent()` into either a Drawer or a Sider from
 * two separate branches, so mobile and desktop drifted apart. It also hand-wrote
 * every level of nesting — the deepest booking report sat four `<Menu.SubMenu>`
 * deep — which is why adding a page meant editing JSX in three places. Here the
 * tree renders recursively and only the container changes.
 */

const RAIL_WIDTH = 'w-[4.25rem]';
const FULL_WIDTH = 'w-[17rem]';

/** Indent per level. Level 0 carries the icon; deeper levels align under it. */
const INDENT = ['pl-3', 'pl-9', 'pl-12', 'pl-14'];

function indentFor(depth: number) {
  return INDENT[Math.min(depth, INDENT.length - 1)];
}

/**
 * Attributes that hide an entry which has not been built yet.
 *
 * The node is left out of the tree entirely; when the roadmap is revealed it
 * comes back carrying `nav-planned` and its phase, and one CSS
 * rule in styles/index.css does the hiding — so the whole roadmap can be
 * revealed for review with
 *   document.documentElement.classList.add('show-planned')
 * and an entry ships by changing one word in nav.config.ts.
 */
function plannedAttrs(item: ResolvedNavItem) {
  if (!item.planned) return {};
  return {
    className: 'nav-planned',
    'data-nav-status': 'planned',
    'data-nav-phase': String(item.phase ?? 'later'),
  };
}

function ItemTag({ tag }: { tag: string }) {
  return (
    <Badge
      variant="secondary"
      className="ml-1 px-1.5 py-0 text-[0.6rem] font-medium"
    >
      {tag}
    </Badge>
  );
}

// ---------------------------------------------------------------------- leaf

function NavLeaf({
  item,
  collapsed,
  depth,
}: {
  item: ResolvedNavItem;
  collapsed: boolean;
  depth: number;
}) {
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);
  const openNavAction = useNavActions((s) => s.open);
  const { t } = useTranslation();
  const Icon = item.icon;
  const showIcon = depth === 0 || collapsed;
  // Not NavLink's own `isActive`: it marks every ancestor path active, so
  // /team lit up alongside /team/timesheets. See useIsActive.
  const active = useIsActive()(item.to ?? '');

  const body = (isActive: boolean) => (
    <>
      {/* Active marker: a rail on the left edge, not a full-bleed block — it
          survives the collapsed rail, where a background alone reads as noise
          between icons. */}
      <span
        aria-hidden
        className={cn(
          'bg-sidebar-primary absolute left-0 h-5 w-[3px] rounded-r-full transition-opacity',
          isActive ? 'opacity-100' : 'opacity-0'
        )}
      />
      {showIcon && Icon && (
        <Icon
          className={cn(
            'size-[1.1rem] shrink-0 transition-colors',
            isActive ? 'text-sidebar-primary' : 'text-sidebar-foreground/60'
          )}
        />
      )}
      {!collapsed && <span className="truncate">{t(item.label)}</span>}
      {!collapsed && item.tag && <ItemTag tag={item.tag} />}
      {!collapsed && item.badge !== undefined && (
        <Badge variant="secondary" className="ml-auto tabular-nums">
          {item.badge}
        </Badge>
      )}
    </>
  );

  const planned = plannedAttrs(item);

  // min-h-11 is 44px, the smallest reliable touch target; on a pointer device
  // the rows tighten back up so a deep tree still fits on one screen.
  const shared = cn(
    'group relative flex w-full items-center gap-3 rounded-lg py-2 pr-3 text-sm transition-colors',
    'min-h-11 lg:min-h-0',
    'focus-visible:ring-sidebar-ring/50 outline-none focus-visible:ring-2',
    collapsed ? 'justify-center px-0' : indentFor(depth),
    depth === 0 ? 'font-medium' : 'text-[0.8125rem] font-normal',
    planned.className
  );

  // An "action" entry runs a handler instead of navigating. The original had
  // two: Change API URL and New Business, both of which opened a modal.
  if (item.action) {
    return (
      <Wrap collapsed={collapsed} label={t(item.label)}>
        <button
          type="button"
          data-nav-status={planned['data-nav-status']}
          data-nav-phase={planned['data-nav-phase']}
          onClick={() => {
            setMobileOpen(false);
            // Change API URL has no dialog yet; New Business does.
            if (item.action === 'newBusiness') {
              openNavAction(item.action);
            } else {
              toast.info(t('shell.notImplemented', { name: t(item.label) }));
            }
          }}
          className={cn(
            shared,
            'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
          )}
        >
          {body(false)}
        </button>
      </Wrap>
    );
  }

  if (item.disabled || !item.to) {
    return (
      <Wrap collapsed={collapsed} label={t(item.label)}>
        <span
          aria-disabled
          data-nav-status={planned['data-nav-status']}
          data-nav-phase={planned['data-nav-phase']}
          className={cn(shared, 'text-sidebar-foreground/35 cursor-not-allowed')}
        >
          {body(false)}
        </span>
      </Wrap>
    );
  }

  return (
    <Wrap collapsed={collapsed} label={t(item.label)} badge={item.badge}>
      <NavLink
        to={item.to}
        data-nav-status={planned['data-nav-status']}
        data-nav-phase={planned['data-nav-phase']}
        onClick={() => setMobileOpen(false)}
        className={cn(
          shared,
          'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          active &&
            'bg-sidebar-accent text-sidebar-accent-foreground font-semibold'
        )}
      >
        {body(active)}
      </NavLink>
    </Wrap>
  );
}

/** In the rail the label is gone, so it has to come back as a tooltip. */
function Wrap({
  collapsed,
  label,
  badge,
  children,
}: {
  collapsed: boolean;
  label: string;
  badge?: string | number;
  children: React.ReactNode;
}) {
  if (!collapsed) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" className="flex items-center gap-2">
        {label}
        {badge !== undefined && (
          <span className="opacity-70 tabular-nums">{badge}</span>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

// --------------------------------------------------------------------- group

function NavGroup({
  item,
  collapsed,
  depth,
}: {
  item: ResolvedNavItem;
  collapsed: boolean;
  depth: number;
}) {
  const { openGroups, toggleGroup, openGroup } = useSidebarStore();
  const containsActive = useContainsActive();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const Icon = item.icon;

  const active = containsActive(item);
  const isOpen = openGroups.includes(item.key);
  const planned = plannedAttrs(item);

  // Deep-linking into a descendant must reveal it, at any depth. The old
  // sidebar left the parent collapsed, so a bookmarked page showed a menu with
  // nothing highlighted.
  useEffect(() => {
    if (active && !isOpen) openGroup(item.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, active]);

  // The rail has no room to expand; clicking a group opens the sidebar instead.
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-nav-status={planned['data-nav-status']}
            data-nav-phase={planned['data-nav-phase']}
            onClick={() => useSidebarStore.getState().setCollapsed(false)}
            className={cn(
              'group relative flex w-full items-center justify-center rounded-lg py-2 transition-colors',
              'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              active && 'bg-sidebar-accent text-sidebar-accent-foreground',
              planned.className
            )}
          >
            <span
              aria-hidden
              className={cn(
                'bg-sidebar-primary absolute left-0 h-5 w-[3px] rounded-r-full',
                active ? 'opacity-100' : 'opacity-0'
              )}
            />
            {Icon && (
              <Icon
                className={cn(
                  'size-[1.1rem] shrink-0',
                  active ? 'text-sidebar-primary' : 'text-sidebar-foreground/60'
                )}
              />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {t(item.label)}
          <span className="ml-1 opacity-60">({item.children?.length})</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={() => toggleGroup(item.key)}
      data-nav-status={planned['data-nav-status']}
      data-nav-phase={planned['data-nav-phase']}
      className={planned.className}
    >
      <CollapsibleTrigger
        className={cn(
          'group relative flex w-full items-center gap-3 rounded-lg py-2 pr-2 text-sm transition-colors',
          'min-h-11 lg:min-h-0',
          'text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
          'focus-visible:ring-sidebar-ring/50 outline-none focus-visible:ring-2',
          indentFor(depth),
          depth === 0 ? 'font-medium' : 'text-[0.8125rem]',
          active && 'text-sidebar-accent-foreground font-semibold'
        )}
      >
        <span
          aria-hidden
          className={cn(
            'bg-sidebar-primary absolute left-0 h-5 w-[3px] rounded-r-full transition-opacity',
            active && !isOpen ? 'opacity-100' : 'opacity-0'
          )}
        />
        {depth === 0 && Icon && (
          <Icon
            className={cn(
              'size-[1.1rem] shrink-0',
              active ? 'text-sidebar-primary' : 'text-sidebar-foreground/60'
            )}
          />
        )}
        <span className="truncate">{t(item.label)}</span>
        <ChevronRight
          className={cn(
            'text-sidebar-foreground/50 ml-auto size-4 shrink-0 transition-transform duration-200',
            isOpen && 'rotate-90'
          )}
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up overflow-hidden">
        <div className="relative mt-0.5 space-y-0.5">
          {/* Guide line, so a long child list stays visually tied to its parent. */}
          <span
            aria-hidden
            className="bg-sidebar-border absolute top-1 bottom-1 w-px"
            style={{ left: depth === 0 ? '1.55rem' : `${2.4 + depth * 0.6}rem` }}
          />
          {item.children?.map((child) => (
            <NavNode
              key={child.key}
              item={child}
              collapsed={false}
              depth={depth + 1}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function NavNode({
  item,
  collapsed,
  depth,
}: {
  item: ResolvedNavItem;
  collapsed: boolean;
  depth: number;
}) {
  return item.children?.length ? (
    <NavGroup item={item} collapsed={collapsed} depth={depth} />
  ) : (
    <NavLeaf item={item} collapsed={collapsed} depth={depth} />
  );
}

// -------------------------------------------------------------------- shell

function SidebarContent({
  collapsed,
  onOpenSearch,
}: {
  collapsed: boolean;
  onOpenSearch: () => void;
}) {
  const sections = useVisibleNavigation();
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col">
      <BusinessSwitcher collapsed={collapsed} />

      {/* ~120 pages is well past the point where scanning works. Search is the
          primary way in; the tree is the map. */}
      <div className={cn('px-3 pb-2', collapsed && 'px-2')}>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onOpenSearch}
                className="text-sidebar-foreground/70 hover:bg-sidebar-accent w-full"
              >
                <Search className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {t('shell.searchPagesShortcut')}
            </TooltipContent>
          </Tooltip>
        ) : (
          <button
            type="button"
            onClick={onOpenSearch}
            className={cn(
              'text-muted-foreground flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm',
              'bg-background/60 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors',
              'focus-visible:ring-sidebar-ring/50 outline-none focus-visible:ring-2'
            )}
          >
            <Search className="size-4 shrink-0" />
            <span className="truncate">{t('shell.searchPlaceholder')}</span>
            <kbd className="bg-muted text-muted-foreground ml-auto rounded border px-1.5 py-0.5 font-mono text-[0.65rem]">
              ⌘K
            </kbd>
          </button>
        )}
      </div>

      <nav
        aria-label={t('shell.mainNavigation')}
        className={cn(
          'flex-1 space-y-4 overflow-y-auto px-3 pb-4',
          collapsed && 'px-2'
        )}
      >
        {sections.map((section) => (
          <div
            key={section.key}
            data-nav-status={section.planned ? 'planned' : undefined}
            className={cn('space-y-0.5', section.planned && 'nav-planned')}
          >
            {section.label && !collapsed && (
              <p className="text-sidebar-foreground/45 px-3 pt-2 pb-1 text-[0.68rem] font-semibold tracking-wider uppercase">
                {t(section.label)}
              </p>
            )}
            {section.label && collapsed && (
              <div className="bg-sidebar-border mx-auto my-2 h-px w-6" />
            )}
            {section.items.map((item) => (
              <NavNode
                key={item.key}
                item={item}
                collapsed={collapsed}
                depth={0}
              />
            ))}
          </div>
        ))}
      </nav>
    </div>
  );
}

export function AppSidebar({ onOpenSearch }: { onOpenSearch: () => void }) {
  const isCompact = useIsCompact();
  const { t } = useTranslation();
  const { collapsed, mobileOpen, setMobileOpen, toggleCollapsed } =
    useSidebarStore();

  // Mobile gets a real drawer; desktop an inline column. Same content either way.
  if (isCompact) {
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          showClose={false}
          className="bg-sidebar text-sidebar-foreground w-[18rem] p-0"
        >
          <SheetTitle className="sr-only">{t('shell.navigation')}</SheetTitle>
          <SidebarContent collapsed={false} onOpenSearch={onOpenSearch} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        'bg-sidebar text-sidebar-foreground border-sidebar-border relative flex h-svh shrink-0 flex-col border-r',
        'transition-[width] duration-200 ease-out',
        collapsed ? RAIL_WIDTH : FULL_WIDTH
      )}
    >
      <SidebarContent collapsed={collapsed} onOpenSearch={onOpenSearch} />

      {/* Collapse control lives at the bottom of the sidebar it controls, not in
          the header where the old hamburger was — the hamburger is the mobile
          drawer's job and one control should not mean two things. */}
      <div className="border-sidebar-border border-t p-2">
        <Button
          variant="ghost"
          size={collapsed ? 'icon' : 'sm'}
          onClick={toggleCollapsed}
          aria-label={
            collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')
          }
          className={cn(
            'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground w-full',
            !collapsed && 'justify-start gap-2'
          )}
        >
          {collapsed ? (
            <PanelLeftOpen className="size-4" />
          ) : (
            <>
              <PanelLeftClose className="size-4" />
              <span className="text-xs">{t('shell.collapse')}</span>
            </>
          )}
        </Button>
      </div>
    </aside>
  );
}
